import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  FaTimes, FaChild, FaSpinner, FaCheck, FaUser, FaEnvelope, FaPhone,
  FaGraduationCap, FaClock, FaLightbulb, FaMapMarkerAlt, FaThumbsUp,
  FaThumbsDown, FaClipboardList
} from 'react-icons/fa';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/* Same wording as the client-side test — must stay in sync with
   /app/frontend/src/components/ChildFutureReadyTest.js */
const QUESTIONS = [
  "Have you had a career planning conversation with your child?",
  "Do you know your child's interests, skills, & personality strengths?",
  "Have you set a career goal or direction for your child based on future demand?",
  "How confident are you in funding your child's higher education?",
  "Do you have a dedicated financial plan or education fund for your child?",
  "Are you aware of the rising costs of Indian & international education?",
  "Have you considered psychometric career tests or career counseling?",
  "Do you track or review your investments related to your child's goals?",
  "Do you want your child to follow a planned career path or decide on their own?",
  "Would you like a free consultation on career & financial readiness for your child?",
];

const AnswerPill = ({ value }) => {
  const styles = {
    4: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    3: 'bg-sky-50 text-sky-700 border-sky-200',
    2: 'bg-amber-50 text-amber-700 border-amber-200',
    1: 'bg-red-50 text-red-700 border-red-200',
    0: 'bg-slate-100 text-slate-600 border-slate-200',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border ${styles[value] || styles[0]}`}>
      {value}/4
    </span>
  );
};

const StatusChip = ({ status }) => {
  const map = {
    pending:  { bg: 'bg-amber-50 text-amber-700 border-amber-200',   label: 'Pending Review' },
    approved: { bg: 'bg-emerald-50 text-emerald-700 border-emerald-200', label: 'Approved' },
    rejected: { bg: 'bg-red-50 text-red-700 border-red-200',         label: 'Rejected' },
  };
  const cfg = map[status] || map.pending;
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border ${cfg.bg}`}>{cfg.label}</span>;
};

/**
 * ChildFutureTestModal
 *
 * Loads `GET /api/advisor/client/{clientId}/child-future-tests` and renders
 * the client's Child Future Ready Test submissions (child info, inputs +
 * outputs) for the advisor. Supports multi-submission browsing.
 */
const ChildFutureTestModal = ({ clientId, clientName, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [activeIdx, setActiveIdx] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(
          `${API}/advisor/client/${clientId}/child-future-tests`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setData(res.data);
      } catch (e) {
        setError(e.response?.data?.detail || 'Unable to load test submissions');
      } finally {
        setLoading(false);
      }
    })();
  }, [clientId]);

  const test = data?.history?.[activeIdx];

  // Interpretation preview — matches backend logic when approved
  const previewInterpretation = (score) => {
    if (score >= 35) return { label: 'Proactive Planner', color: 'text-emerald-600', bg: 'bg-emerald-50 border-emerald-200' };
    if (score >= 26) return { label: 'Aware but Needs Action', color: 'text-sky-600', bg: 'bg-sky-50 border-sky-200' };
    if (score >= 15) return { label: 'At Risk — Act Now', color: 'text-amber-600', bg: 'bg-amber-50 border-amber-200' };
    return { label: 'Unprepared but Not Too Late', color: 'text-red-600', bg: 'bg-red-50 border-red-200' };
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="cft-modal">
      <div
        className="bg-white rounded-2xl border border-[#EDE9DE] max-w-4xl w-full max-h-[90vh] overflow-hidden shadow-2xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-[#EDE9DE] bg-gradient-to-r from-[#0F4C5C] to-[#093039]">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-[#D4A84F] flex items-center justify-center">
              <FaChild className="text-[#263238]" size={20} />
            </div>
            <div>
              <p className="font-poppins text-white text-lg font-semibold leading-tight">Is Your Child Future Ready?</p>
              <p className="text-white/70 text-xs">{clientName}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-white/70 hover:text-white" data-testid="cft-close">
            <FaTimes size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto p-6 flex-1">
          {loading && (
            <div className="flex items-center justify-center py-16 text-[#4E5B65]">
              <FaSpinner className="animate-spin mr-3" /> Loading submissions…
            </div>
          )}
          {!loading && error && (
            <div className="p-4 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>
          )}
          {!loading && !error && !test && (
            <div className="text-center py-12 text-[#78838C]">This client hasn&apos;t submitted the Child Future Ready Test yet.</div>
          )}

          {!loading && !error && test && (
            <>
              {/* Submission selector */}
              {data.history.length > 1 && (
                <div className="mb-5 flex items-center gap-2 flex-wrap">
                  <span className="text-xs text-[#78838C] mr-1">Submissions:</span>
                  {data.history.map((h, i) => (
                    <button
                      key={h.id}
                      onClick={() => setActiveIdx(i)}
                      className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                        i === activeIdx
                          ? 'bg-[#0F4C5C] text-white border-[#0F4C5C]'
                          : 'bg-white text-[#4E5B65] border-[#EDE9DE] hover:border-[#0F4C5C]/40'
                      }`}
                    >
                      {new Date(h.submitted_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </button>
                  ))}
                </div>
              )}

              {/* Client + Child meta */}
              <div className="grid sm:grid-cols-2 gap-3 mb-5">
                <div className="p-3 bg-[#F8F6F0] rounded-lg border border-[#EDE9DE] text-sm text-[#263238] flex items-center gap-2">
                  <FaUser className="text-[#0F4C5C]" size={12} /> Parent: {data.client.name || '—'}
                </div>
                <div className="p-3 bg-[#F8F6F0] rounded-lg border border-[#EDE9DE] text-sm text-[#263238] flex items-center gap-2">
                  <FaEnvelope className="text-[#0F4C5C]" size={12} /> {data.client.email || '—'}
                </div>
                <div className="p-3 bg-[#F8F6F0] rounded-lg border border-[#EDE9DE] text-sm text-[#263238] flex items-center gap-2">
                  <FaPhone className="text-[#0F4C5C]" size={12} /> {data.client.phone || '—'}
                </div>
                {(data.client.city || data.client.state) && (
                  <div className="p-3 bg-[#F8F6F0] rounded-lg border border-[#EDE9DE] text-sm text-[#263238] flex items-center gap-2">
                    <FaMapMarkerAlt className="text-[#0F4C5C]" size={12} /> {[data.client.city, data.client.state].filter(Boolean).join(', ')}
                  </div>
                )}
                <div className="p-3 bg-[#FBF6EA] rounded-lg border border-[#D4A84F]/45 text-sm text-[#263238] flex items-center gap-2 sm:col-span-2">
                  <FaChild className="text-[#B18A3A]" size={12} />
                  <strong className="text-[#0F4C5C]">Child:</strong> {test.child_name} · Age {test.child_age} · {test.child_class}
                </div>
              </div>

              {/* Score + Status + Interpretation */}
              {(() => {
                const preview = previewInterpretation(test.total_score);
                return (
                  <div className="grid sm:grid-cols-3 gap-3 mb-5">
                    <div className="p-4 bg-white rounded-xl border border-[#EDE9DE] shadow-sm text-center">
                      <p className="text-xs text-[#78838C] uppercase tracking-wide">Score</p>
                      <p className="font-poppins text-3xl font-semibold text-[#0F4C5C]">{test.total_score}<span className="text-base text-[#78838C]">/{test.max_score || 40}</span></p>
                    </div>
                    <div className={`p-4 rounded-xl border shadow-sm text-center ${preview.bg}`}>
                      <p className="text-xs uppercase tracking-wide opacity-80">Category</p>
                      <p className={`font-poppins text-base font-semibold mt-1 leading-tight ${preview.color}`}>{preview.label}</p>
                    </div>
                    <div className="p-4 bg-white rounded-xl border border-[#EDE9DE] shadow-sm text-center flex flex-col items-center justify-center">
                      <p className="text-xs text-[#78838C] uppercase tracking-wide mb-2">Status</p>
                      <StatusChip status={test.status} />
                    </div>
                  </div>
                );
              })()}

              {/* Approved-only advisor output */}
              {test.status === 'approved' && (
                <div className="mb-5 p-4 rounded-xl bg-[#F8F6F0] border border-[#EDE9DE]">
                  <p className="text-xs uppercase tracking-wide text-[#78838C] mb-1 flex items-center gap-1.5">
                    <FaLightbulb className="text-[#D4A84F]" size={11} /> Advisor Interpretation
                  </p>
                  <p className="text-[#263238] text-sm leading-relaxed mb-3">{test.interpretation}</p>

                  {test.recommendation_details && (
                    <div className="mt-3 p-3 bg-white rounded-lg border border-[#EDE9DE]">
                      <p className="text-xs uppercase tracking-wide text-[#78838C] mb-1">Recommended Service</p>
                      <p className="text-[#0F4C5C] font-semibold text-sm mb-1 capitalize">{(test.recommendation_service || '').replace('_', ' ')}</p>
                      <p className="text-[#4E5B65] text-xs leading-relaxed">{test.recommendation_details}</p>
                    </div>
                  )}

                  {test.next_steps?.length > 0 && (
                    <div className="mt-3">
                      <p className="text-xs uppercase tracking-wide text-[#78838C] mb-2 flex items-center gap-1.5">
                        <FaClipboardList size={11} /> Next Steps
                      </p>
                      <ul className="space-y-1.5">
                        {test.next_steps.map((step, i) => (
                          <li key={i} className="text-sm text-[#263238] flex items-start gap-2">
                            <FaCheck className="text-[#16A085] mt-1 flex-shrink-0" size={10} />
                            <span>{step}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              {/* Rejection reason */}
              {test.status === 'rejected' && test.rejection_reason && (
                <div className="mb-5 p-4 rounded-xl bg-red-50 border border-red-200 flex items-start gap-3">
                  <FaThumbsDown className="text-red-600 mt-1 flex-shrink-0" />
                  <div>
                    <p className="text-xs uppercase tracking-wide text-red-700 mb-1">Rejection Reason</p>
                    <p className="text-red-800 text-sm">{test.rejection_reason}</p>
                  </div>
                </div>
              )}

              {test.status === 'pending' && (
                <div className="mb-5 p-4 rounded-xl bg-amber-50 border border-amber-200 flex items-start gap-3">
                  <FaThumbsUp className="text-amber-600 mt-1 flex-shrink-0" />
                  <p className="text-amber-800 text-sm">
                    Awaiting your review. Use the Pending Tests section on your dashboard to approve or reject.
                  </p>
                </div>
              )}

              {/* Per-question answers */}
              <div>
                <p className="text-xs uppercase tracking-wide text-[#78838C] mb-2">Per-question Answers</p>
                <div className="space-y-2">
                  {test.answers?.map((qa) => (
                    <div key={qa.question_number} className="p-3 rounded-lg bg-white border border-[#EDE9DE] flex items-start gap-3">
                      <span className="w-6 h-6 rounded-md bg-[#0F4C5C] text-white text-xs font-semibold flex items-center justify-center flex-shrink-0">
                        {qa.question_number}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-[#263238] leading-snug">{QUESTIONS[qa.question_number - 1] || `Question ${qa.question_number}`}</p>
                        {qa.answer_text && (
                          <p className="text-xs text-[#78838C] mt-1 italic">Selected: {qa.answer_text}</p>
                        )}
                      </div>
                      <AnswerPill value={qa.answer_value} />
                    </div>
                  ))}
                </div>
              </div>

              <p className="mt-4 text-xs text-[#78838C] flex items-center gap-1.5">
                <FaClock size={10} />
                Submitted on {new Date(test.submitted_at).toLocaleString('en-IN', {
                  day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
                })}
                {test.approved_at && (
                  <span className="ml-2 text-emerald-600">
                    · Approved on {new Date(test.approved_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </span>
                )}
              </p>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-[#EDE9DE] bg-[#F8F6F0] flex items-center justify-between">
          <p className="text-xs text-[#78838C]">Use this data to guide the Shiksha Sankalp / Goal Manthan consultation.</p>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-[#0F4C5C] text-white text-sm font-semibold rounded-lg hover:bg-[#093039] transition-colors"
            data-testid="cft-done"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChildFutureTestModal;
